import { ProductInterface } from '../../../services/exports/Interfaces';
import classnames from 'classnames';
import CustomLabel from '../../labels/CustomLabel';
import Theme from '../../Theme';
import PriceComparison from '../../menu/PriceComparison';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import useProductIdentifiers from '../../../hooks/menu/useProductIdentifiers';
import useBasket from '../../../hooks/basket/useBasket';
import SeoResource from '../../../services/resources/SeoResource';
import useMenu from '../../../hooks/menu/useMenu';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import RadioButton from '../../buttons/RadioButton';
import useTheme from '../../../hooks/ui/useTheme';
import { WithActualPrice } from '../../../providers/MenuProvider';
import useStorefront from '../../../hooks/storefront/useStorefront';
import useScreenType from '../../../hooks/utility/useScreenType';

interface Props {
  product: WithActualPrice<ProductInterface>;
  toggleProduct: () => void;
  className?: string;
}

const SingleSelectProduct = (props: Props): JSX.Element => {
  const { isDesktop } = useScreenType();

  return isDesktop ? <SingleSelectProduct.Web {...props} /> : <SingleSelectProduct.Mobile {...props} />;
};

export default SingleSelectProduct;

SingleSelectProduct.Web = ({ product, toggleProduct }: Props): JSX.Element => {
  const { productHeight } = useMenu();
  const { isStorefront } = useStorefront();

  return (
    <article
      className={classnames(
        'w-full flex flex-row select-none relative overflow-hidden border rounded-2xl gap-2 cursor-pointer',
        {
          '!rounded-[8px]': isStorefront,
        },
      )}
      style={{ height: productHeight }}
      onClick={() => toggleProduct && toggleProduct()}
    >
      <SingleSelectProduct.LeftColumn product={product} isMobile={false} />
      <SingleSelectProduct.RightColumn product={product} />
      <SingleSelectProduct.SoldOutOverlay product={product} />
    </article>
  );
};

SingleSelectProduct.Mobile = ({ product, toggleProduct }: Props) => {
  const { productHeight } = useMenu();

  return (
    <article
      className={classnames(
        'w-full flex flex-row items-center select-none relative overflow-hidden gap-2 py-md cursor-pointer',
      )}
      style={{ height: productHeight }}
      onClick={() => toggleProduct && toggleProduct()}
    >
      <SingleSelectProduct.LeftColumn product={product} isMobile={true} />
      <SingleSelectProduct.RightColumn product={product} />
      <SingleSelectProduct.SoldOutOverlay product={product} />
    </article>
  );
};

SingleSelectProduct.LeftColumn = ({ product }: { product: WithActualPrice<ProductInterface>; isMobile: boolean }) => {
  const { isDesktop } = useScreenType();
  return (
    <div
      className={classnames('flex flex-col flex-1 shrink-0 relative justify-between', {
        'py-md px-md': isDesktop,
      })}
    >
      <div className={'flex flex-1 flex-col justify-between lg:gap-y-sm gap-y-xs'}>
        <div className={classnames('flex flex-col space-y-xs', { '!space-y-[4px]': !isDesktop })}>
          <h6
            className={classnames('font-normal text-[15px] text-brand-text-primary line-clamp-1 break-words', {
              'font-semibold': !isDesktop,
            })}
          >
            {product?.name}
          </h6>
          <SingleSelectProduct.PriceRow product={product} />
          {product?.description && (
            <p
              className={classnames('text-brand-text-secondary font-medium line-clamp-3 break-words text-xs', {
                '!line-clamp-2': !isDesktop,
              })}
            >
              {product.description}
            </p>
          )}
        </div>
        <SingleSelectProduct.Labels product={product} />
      </div>
    </div>
  );
};

SingleSelectProduct.RightColumn = ({ product }: { product: ProductInterface }): JSX.Element => {
  const { isMobileApp } = useMobileApp();

  if (!product?.thumbnail_url) {
    return (
      <div
        className={classnames('flex h-full justify-end w-[50px] overflow-hidden relative', {
          'py-md px-sm': !isMobileApp,
        })}
      >
        <SingleSelectProduct.AddButton product={product} />
      </div>
    );
  }

  return <SingleSelectProduct.ProductImage product={product} />;
};

SingleSelectProduct.SoldOutOverlay = ({ product }: { product: ProductInterface }) => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Components:Menu:SingleSelectMenuItems',
  });
  const { isMobileApp } = useMobileApp();

  if (product?.is_available) {
    return null;
  }

  return (
    <div className={classnames('top-0 left-0 absolute h-full w-full z-10', { 'py-md': isMobileApp })}>
      <div className="flex flex-col items-center justify-center h-full rounded-xl bg-black bg-opacity-50">
        <div className="flex flex-col absolute inset-0 items-center justify-center">
          <p className="text-white text-center">{t('labels.sold_out')}</p>
        </div>
      </div>
    </div>
  );
};

SingleSelectProduct.PriceRow = ({ product }: { product: WithActualPrice<ProductInterface> }) => {
  const theme = useTheme();

  return (
    <div className={'flex flex-row justify-between items-center w-full relative'}>
      <div className="h-full flex flex-col justify-end">
        <PriceComparison
          actualPrice={product?.actual_price}
          originalPrice={product?.base_price}
          actualPriceStyle={{
            fontWeight: 'semibold',
            color: theme.color.brand.text.primary,
            fontSize: 15,
          }}
          originalPriceStyle={{
            fontWeight: 'semibold',
            fontSize: 15,
          }}
        />
      </div>
    </div>
  );
};

SingleSelectProduct.ProductImage = ({ product }: { product: ProductInterface }): JSX.Element | null => {
  const { isDesktop } = useScreenType();

  return (
    <div
      className={classnames('flex h-full shrink-none overflow-hidden relative', {
        'lg:py-none lg:pr-none py-md pr-sm': isDesktop,
        'w-[120px] h-[120px]': !isDesktop,
      })}
    >
      <div className={'relative h-full w-full my-auto aspect-square object-cover'}>
        <img
          src={product?.thumbnail_url}
          alt={SeoResource.toAltText(`${product?.name} image`)}
          className={'relative h-full w-full my-auto aspect-square object-cover lg:rounded-none rounded-lg'}
        />
      </div>
      <SingleSelectProduct.AddButton product={product} className="absolute top-2 right-2" />
    </div>
  );
};

SingleSelectProduct.AddButton = ({
  product,
  className,
}: {
  product: ProductInterface;
  className?: string;
}): JSX.Element => {
  const { freeItem } = useBasket();
  const isSelected = useMemo(() => freeItem?.product_id === product?.id, [freeItem?.product_id, product?.id]);

  return (
    <div className={classnames('flex-none', className)}>
      <RadioButton isSelected={isSelected} className="w-[26px] h-[26px]" innerClassName="w-[18px] h-[18px]" />
    </div>
  );
};

SingleSelectProduct.Labels = ({ product }: { product: ProductInterface }): JSX.Element => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Components:Menu:SingleSelectMenuItems',
  });
  const { emojis } = useProductIdentifiers(product);

  return (
    <div className={'flex flex-row items-center gap-x-2 w-fit'}>
      {emojis.isNotEmpty() && <p className="mb-xs text-right">{emojis.implode(' ')}</p>}
      <CustomLabel
        className="text-xs w-fit px-lg"
        backgroundColor={Theme.color.green['300']}
        textColor={Theme.color.primary}
        label={t('labels.free')}
      />
    </div>
  );
};
